/**
 * Centralized Supabase configuration
 * 
 * Benefits:
 * - Single source of truth for Supabase connection details
 * - Easier key rotation and environment management
 * - Simplifies testing and server-side client creation
 */

/**
 * Development-only connection details. These are public by design (project URL
 * plus publishable/anon key) and are ONLY used when the VITE_SUPABASE_* build
 * variables are absent AND the app is served from a recognised Lovable
 * preview/share host (Share Preview builds ship without a .env file).
 *
 * Any other host must fail loudly rather than silently talking to Development.
 */
const developmentDefaults = {
  url: 'https://ggffinwqhmmcnnltairi.supabase.co',
  anonKey: 'sb_publishable_P1T3opALYEQQgi_UpezP_g_hpYPFZvF',
  projectRef: 'ggffinwqhmmcnnltairi',
} as const;

/**
 * Production connection details (public URL + anon key). Always used when the
 * app is served from a production host, even when VITE_SUPABASE_* build
 * variables are present. Production hosts must NEVER follow a stale .env or
 * fall back to Development.
 */
const productionDefaults = {
  url: 'https://api.twiceread.store',
  anonKey:
    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5oZGJ4eHpxZGVhc2lvZXh6bHNsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDk4ODE0NDksImV4cCI6MjA2NTQ1NzQ0OX0.-WruxNaZxWwHvMlb7pl85gfrSwVF-YWoQvO99cMGUqc',
  projectRef: 'nhdbxxzqdeasioexzlsl',
} as const;

/** Exact hostnames that are production and must use the Production backend. */
const PRODUCTION_HOSTS = new Set<string>([
  'twiceread.store',
  'www.twiceread.store',
  'twiceread.lovable.app',
]);

/** Exact hostnames allowed to use the Development fallback. */
const FALLBACK_ALLOWED_HOSTS = new Set<string>([
  'localhost',
  '127.0.0.1',
  '[::1]',
]);

/** Hostname suffixes (Lovable preview / share / sandbox domains) allowed to use the fallback. */
const FALLBACK_ALLOWED_HOST_SUFFIXES = [
  '.lovable.app',
  '.lovableproject.com',
  '.lovable.dev',
  '.sandbox.lovable.dev',
] as const;

/**
 * The ONLY non-browser context allowed to use the Development fallback is an
 * intentional test run: Vitest sets MODE === 'test', and a test config may set
 * VITE_ALLOW_DEV_FALLBACK=true explicitly. Nothing else (including CI
 * production builds or SSR) may fall through.
 */
const isExplicitTestContext = (): boolean =>
  import.meta.env.MODE === 'test' ||
  String(import.meta.env.VITE_ALLOW_DEV_FALLBACK ?? '').toLowerCase() === 'true';

const currentHost = (): string =>
  typeof window === 'undefined' ? '' : window.location.hostname.toLowerCase();

const isProductionHost = (): boolean => PRODUCTION_HOSTS.has(currentHost());

const isFallbackAllowedHost = (): boolean => {
  if (typeof window === 'undefined') return isExplicitTestContext();
  const host = currentHost();
  if (FALLBACK_ALLOWED_HOSTS.has(host)) return true;
  return FALLBACK_ALLOWED_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
};

type ConfigKey = 'url' | 'anonKey' | 'projectRef';

const resolve = (envValue: string | undefined, key: ConfigKey, varName: string): string => {
  // Production hosts are checked FIRST and win over any baked-in .env value:
  // .env is silently regenerated by the platform's reconciliation process and
  // can drift to another project. Production must never follow a stale .env.
  if (isProductionHost()) return productionDefaults[key];
  if (envValue) return envValue;
  if (isFallbackAllowedHost()) return developmentDefaults[key];
  throw new Error(
    `[supabase/config] ${varName} is not configured for this deployment. ` +
      `Refusing to fall back to the Development backend on host "${
        typeof window !== 'undefined' ? window.location.hostname : 'unknown'
      }". Set VITE_SUPABASE_URL, VITE_SUPABASE_PUBLISHABLE_KEY and ` +
      `VITE_SUPABASE_PROJECT_ID for this environment.`
  );
};

export const supabaseConfig = {
  url: resolve(import.meta.env.VITE_SUPABASE_URL, 'url', 'VITE_SUPABASE_URL'),
  anonKey: resolve(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY, 'anonKey', 'VITE_SUPABASE_PUBLISHABLE_KEY'),
  projectRef: resolve(import.meta.env.VITE_SUPABASE_PROJECT_ID, 'projectRef', 'VITE_SUPABASE_PROJECT_ID'),
} as const;

// Auth configuration
export const authConfig = {
  storage: typeof window !== 'undefined' ? localStorage : undefined,
  persistSession: true,
  autoRefreshToken: true,
  detectSessionInUrl: true,
  // Managed Google sign-in returns an implicit-grant session in the URL when
  // the app is opened outside the embedded Preview. The auth client must use
  // the matching flow so it can consume and remove those tokens on startup.
  flowType: 'implicit' as const,
};

// Realtime configuration
export const realtimeConfig = {
  params: {
    eventsPerSecond: 10,
  },
};

// Global headers
export const globalHeaders = {
  'X-Client-Info': 'twice-read-app',
};
